// Detection: on every poll it fetches index.html fresh (with Cache-Control: no-cache) and compares the <script src> fingerprints 
// against what's in the current DOM. If any current script hash is gone from the new index, a new build has been deployed.
// Reload timing — it does not reload immediately. Instead it waits for the next NavigationStart event. 
// The user will only ever reload at the moment they intentionally click a link to go somewhere — never mid-form-fill, mid-payment, or mid-enrollment.
// Auth interceptor bypass — uses HttpBackend directly so the Bearer token is not sent on the index.html fetch.
// Poll interval — 5 minutes, easily adjustable via the POLL_INTERVAL_MS constant at the top of deployment-poller.service.ts


import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Router, NavigationStart } from '@angular/router';
import { filter, take } from 'rxjs/operators';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

@Injectable({ providedIn: 'root' })
export class DeploymentPollerService implements OnDestroy {

  private readonly http = new HttpClient(inject(HttpBackend)); // bypasses auth interceptor
  private readonly router = inject(Router);

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly currentScripts = this.getScriptSrcs();
  private updateAvailable = false;

  start(): void {
    if (this.intervalId !== null) return;
    this.intervalId = setInterval(() => this.poll(), POLL_INTERVAL_MS);
  }

  private poll(): void {
    // Fetch index.html bypassing any browser cache
    this.http.get('/', { responseType: 'text', headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } })
      .pipe(take(1))
      .subscribe({
        next: html => {
          if (this.hasNewDeployment(html)) {
            this.updateAvailable = true;
            this.reloadOnNextNavigation();
          }
        },
        error: () => { /* swallow — network blip should not affect the user */ }
      });
  }

  private hasNewDeployment(html: string): boolean {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const fetchedScripts = new Set(
      Array.from(doc.querySelectorAll('script[src]'))
        .map(s => s.getAttribute('src') ?? '')
        .filter(s => s.length > 0)
    );
    // A new deployment has landed if any current script src is no longer in the fetched index
    return this.currentScripts.some(src => !fetchedScripts.has(src));
  }

  private reloadOnNextNavigation(): void {
    // Wait for the next NavigationStart, then reload — user has just initiated a route change
    // which means they are between views, minimising disruption
    this.router.events.pipe(
      filter(e => e instanceof NavigationStart),
      take(1)
    ).subscribe(() => window.location.reload());
  }

  private getScriptSrcs(): string[] {
    return Array.from(document.querySelectorAll('script[src]'))
      .map(s => s.getAttribute('src') ?? '')
      .filter(s => s.length > 0);
  }

  ngOnDestroy(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
    }
  }
}
